import { DeleteOutlined, EyeOutlined, PlusOutlined, StopOutlined } from "@ant-design/icons";
import { useNavigate } from "@tanstack/react-router";
import { Button, Input, Popconfirm, Progress, Select, Space, Tag, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { TFunction } from "i18next";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { DataTable } from "@/shared/components/DataTable";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { useCancelTask, useDeleteTask, useTasksList } from "../hooks/useTasks";
import {
	TASK_STATUS_COLORS,
	TASK_STATUSES,
	type Task,
	type TaskListFilters,
	type TaskStatus,
} from "../types";

const { Title } = Typography;

/** Mahalliy vaqtda ko'rsatish — server UTC `Z` yuboradi. */
function formatDateTime(iso: string): string {
	return new Date(iso).toLocaleString("uz-UZ", { dateStyle: "short", timeStyle: "short" });
}

interface TaskRowActionsProps {
	task: Task;
	onOpen: (id: string) => void;
	onCancel: (id: string) => void;
	onDelete: (id: string) => void;
}

/**
 * Qator amallari — alohida komponent: uchta `PermissionGate` va status
 * sharti ustun `render` ida qolsa, `TasksPage` ning kognitiv
 * murakkabligi Biome chegarasidan (15) oshardi (`ZonesPage` naqshi).
 */
function TaskRowActions({ task, onOpen, onCancel, onDelete }: TaskRowActionsProps) {
	const { t } = useTranslation("tasks");

	return (
		<Space>
			<PermissionGate resource="tasks" action="read">
				<Button
					type="text"
					icon={<EyeOutlined />}
					title={t("list.actions.detail")}
					onClick={() => onOpen(task.id)}
				/>
			</PermissionGate>
			{/* ⚠️ `task.canWrite` — `tasks:update`/`delete` RUXSATIDAN alohida:
			    rahbar topshiriqni ijrochi orqali ko'rgan bo'lishi mumkin
			    (egasi bo'lim doirasida EMAS), ruxsat bo'lsa ham backend 404
			    qaytaradi. A2 (yakuniy review I1), `TaskDetailPage` bilan bir
			    xil naqsh. */}
			{task.status === "faol" && task.canWrite && (
				<PermissionGate resource="tasks" action="update">
					<Popconfirm
						title={t("list.cancelConfirm.title")}
						description={t("list.cancelConfirm.description")}
						okText={t("list.cancelConfirm.okText")}
						cancelText={t("list.cancelConfirm.cancelText")}
						okButtonProps={{ danger: true }}
						onConfirm={() => onCancel(task.id)}
					>
						<Button type="text" danger icon={<StopOutlined />} title={t("list.actions.cancel")} />
					</Popconfirm>
				</PermissionGate>
			)}
			{task.canWrite && (
				<PermissionGate resource="tasks" action="delete">
					<Popconfirm
						title={t("list.deleteConfirm.title")}
						description={t("list.deleteConfirm.description")}
						okText={t("list.deleteConfirm.okText")}
						cancelText={t("list.deleteConfirm.cancelText")}
						okButtonProps={{ danger: true }}
						onConfirm={() => onDelete(task.id)}
					>
						<Button type="text" danger icon={<DeleteOutlined />} title={t("list.actions.delete")} />
					</Popconfirm>
				</PermissionGate>
			)}
		</Space>
	);
}

function buildColumns(
	t: TFunction<"tasks">,
	renderActions: (task: Task) => React.ReactNode
): ColumnsType<Task> {
	return [
		{ title: t("list.columns.name"), dataIndex: "name", key: "name" },
		{
			title: t("list.columns.status"),
			dataIndex: "status",
			key: "status",
			render: (status: TaskStatus) => (
				<Tag color={TASK_STATUS_COLORS[status]}>{t(`status.${status}`)}</Tag>
			),
		},
		{
			title: t("list.columns.dueTo"),
			dataIndex: "dueTo",
			key: "dueTo",
			render: (dueTo: string) => formatDateTime(dueTo),
		},
		{
			title: t("list.columns.targetCount"),
			dataIndex: "targetCount",
			key: "targetCount",
		},
		{
			/**
			 * ⚠️ Ikki sanoq ATAYLAB birga ko'rsatiladi: «yetib borgan»
			 * ingest quvuridan (avtomatik), «bajarilgan» esa xodimning
			 * tugmasidan. Ular bir xil son EMAS va farqi rahbar uchun
			 * ma'noli signal.
			 */
			title: t("list.columns.arrived"),
			key: "arrived",
			render: (_: unknown, task: Task) => (
				<Space size="small">
					<span>
						{task.arrivedCount} / {task.assigneeCount}
					</span>
					<Progress
						type="line"
						percent={
							task.assigneeCount === 0
								? 0
								: Math.round((task.arrivedCount / task.assigneeCount) * 100)
						}
						size="small"
						showInfo={false}
						style={{ width: 80 }}
					/>
				</Space>
			),
		},
		{ title: t("list.columns.completedCount"), dataIndex: "completedCount", key: "completedCount" },
		{
			title: t("list.columns.actions"),
			key: "actions",
			width: 140,
			render: (_: unknown, task: Task) => renderActions(task),
		},
	];
}

export function TasksPage() {
	const { t } = useTranslation("tasks");
	const navigate = useNavigate();
	const [filters, setFilters] = useState<TaskListFilters>({});
	const query = useTasksList(filters);
	const cancelTask = useCancelTask();
	const deleteTask = useDeleteTask();

	const statusOptions = useMemo(
		() => TASK_STATUSES.map((value) => ({ value, label: t(`status.${value}`) })),
		[t]
	);

	const columns = useMemo(
		() =>
			buildColumns(t, (task) => (
				<TaskRowActions
					task={task}
					onOpen={(id) => navigate({ to: "/tasks/$taskId", params: { taskId: id } })}
					onCancel={(id) => cancelTask.mutate(id)}
					onDelete={(id) => deleteTask.mutate(id)}
				/>
			)),
		[t, navigate, cancelTask, deleteTask]
	);

	return (
		<div className="flex flex-col gap-4">
			<div className="flex items-center justify-between">
				<Title level={3} className="!mb-0">
					{t("list.title")}
				</Title>
				<PermissionGate resource="tasks" action="create">
					<Button
						type="primary"
						icon={<PlusOutlined />}
						onClick={() => navigate({ to: "/tasks/new" })}
					>
						{t("list.newTask")}
					</Button>
				</PermissionGate>
			</div>

			<Space wrap>
				<Input.Search
					allowClear
					placeholder={t("list.searchPlaceholder")}
					style={{ width: 260 }}
					onSearch={(value) => setFilters((prev) => ({ ...prev, q: value }))}
				/>
				<Select
					allowClear
					placeholder={t("list.statusPlaceholder")}
					style={{ width: 180 }}
					options={statusOptions}
					value={filters.status}
					onChange={(status) => setFilters((prev) => ({ ...prev, status }))}
				/>
			</Space>

			<DataTable<Task> rowKey="id" columns={columns} query={query} />
		</div>
	);
}
